import { Injectable, signal } from '@angular/core';
import {
  GooglePay,
  ApplePay,
  IGooglePaySessionData,
  IApplePaySessionData,
  IGooglePayData,
  IApplePayData,
  IAuthorizeResult,
} from '@bambora/wallet';
import { WalletSession } from '@tabify/models/types';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class BamboraWalletService {
  /** Processing state */
  private _processing = signal(false);
  readonly processing = this._processing.asReadonly();

  /**
   * Start Google Pay payment flow with pre-fetched session from payTransaction response.
   *
   * `gatewayMerchantId` is the per-branch Worldline merchant ID resolved by
   * the caller from `partnerBranchId` + `environment.worldline.branchMerchantIds`.
   */
  async startGooglePay(params: {
    session: WalletSession;
    merchantName: string;
    amount: number;
    currencyCode: string;
    countryCode: string;
    gatewayMerchantId: string;
  }): Promise<IAuthorizeResult | void> {
    if (this._processing()) {
      throw new Error('Payment already in progress');
    }

    this._processing.set(true);

    try {
      const googlePayData: IGooglePayData = {
        merchantInfo: {
          merchantId: environment.worldline.googlePayMerchantId,
          merchantName: params.merchantName,
        },
        allowedPaymentMethods: [
          {
            parameters: {
              allowedCardNetworks: ['VISA', 'MASTERCARD'],
            },
            tokenizationSpecification: {
              parameters: {
                gatewayMerchantId: params.gatewayMerchantId,
              },
            },
          },
        ],
        transactionInfo: {
          totalPrice: params.amount.toFixed(2),
          currencyCode: params.currencyCode,
          countryCode: params.countryCode,
        },
      };

      const sessionData = this.convertSessionDataToObject(params.session);

      const googlePay = await GooglePay.create(
        {
          sessionProvider: {
            fetchSession: async () => ({ data: sessionData }),
          },
          clientConfiguration: {
            environment: environment.worldline
              .googlePayEnvironment as google.payments.api.Environment,
          },
        },
        googlePayData,
      );

      const result = await googlePay.start();

      // Payment authorized (approved or declined) - redirect to accept/decline URL
      if (result?.authorizeResult !== undefined && result?.redirectUrl) {
        window.location.href = result.redirectUrl;
        return result;
      }

      // Payment still processing - redirect to wait URL
      if (
        result?.authorizeResult === undefined &&
        result?.wait &&
        result?.redirectUrl
      ) {
        window.location.href = result.redirectUrl;
        return result;
      }

      return result;
    } finally {
      this._processing.set(false);
    }
  }

  /**
   * Start Apple Pay payment flow with pre-fetched session from payTransaction response.
   *
   * `gatewayMerchantId` is the per-branch Worldline merchant ID resolved by
   * the caller from `partnerBranchId` + `environment.worldline.branchMerchantIds`.
   * Used both as Apple Pay's `merchantInfo.merchantId` (what Bambora's SDK
   * reads here) and as the tokenization-spec `gatewayMerchantId`.
   */
  async startApplePay(params: {
    session: WalletSession;
    merchantName: string;
    amount: number;
    currencyCode: string;
    countryCode: string;
    gatewayMerchantId: string;
  }): Promise<IAuthorizeResult | void> {
    if (this._processing()) {
      throw new Error('Payment already in progress');
    }

    this._processing.set(true);

    try {
      const applePayData: IApplePayData = {
        currencyCode: params.currencyCode,
        countryCode: params.countryCode,
        total: {
          amount: params.amount.toFixed(2),
          label: params.merchantName,
        },
        merchantInfo: {
          merchantId: params.gatewayMerchantId,
          merchantName: params.merchantName,
        },
        supportedNetworks: ['VISA', 'MASTERCARD'],
        allowedPaymentMethods: [
          {
            parameters: {
              supportedNetworks: ['VISA', 'MASTERCARD'],
            },
            tokenizationSpecification: {
              parameters: {
                gatewayMerchantId: params.gatewayMerchantId,
              },
            },
          },
        ],
      };

      const sessionData = this.convertSessionDataToObject(
        params.session,
        'applePay',
      );

      const applePay = await ApplePay.create(
        {
          sessionProvider: {
            fetchSession: async () => ({
              data: sessionData as IApplePaySessionData,
            }),
          },
        },
        applePayData,
      );

      const result = await applePay.start();

      // Payment authorized (approved or declined) - redirect to accept/decline URL
      if (result?.authorizeResult !== undefined && result?.redirectUrl) {
        window.location.href = result.redirectUrl;
        return result;
      }

      // Payment still processing - redirect to wait URL
      if (
        result?.authorizeResult === undefined &&
        result?.wait &&
        result?.redirectUrl
      ) {
        window.location.href = result.redirectUrl;
        return result;
      }

      return result;
    } finally {
      this._processing.set(false);
    }
  }

  /**
   * Convert session data array to object format expected by Bambora SDK
   */
  private convertSessionDataToObject(
    session: WalletSession,
    type: 'googlePay' | 'applePay' = 'googlePay',
  ): IGooglePaySessionData | IApplePaySessionData {
    const sessionData: Record<string, string> = {};

    for (const item of session.data) {
      sessionData[item.key] = item.value;
    }

    if (type === 'googlePay') {
      return sessionData as unknown as IGooglePaySessionData;
    }

    return sessionData as unknown as IApplePaySessionData;
  }
}
